<template>
    <div>
        <h1 class="display-heading">Manage Company Credit</h1>

        <div class="card mb-5">
            <div class="card-body">
                <h5 class="card-title">Company Credit Balance</h5>
                <div class="ewallet-display">RM {{creditBalance.toFixed(2)}}</div>
                <button class="btn btn-primary btn-primary-insv" @click="initiateAddCredit">Add Credit</button>

                <hr>

                <h5 class="card-title">Low Credit Balance Notification</h5>
                <p>
                    You will get an email notification when your company credit balance becomes lower than the set threshold.
                </p>
                <p>
                    The minimum threshold level is RM {{ companyCreditThreshold.default.toFixed(2) }}, and you have the option to configure it to a higher threshold level depending on your employee size.
                </p>

                <div class="row">
                    <div class="col-md-6">
                        <form data-vv-scope="formLowCreditThreshold" @submit.prevent="submitCompanyLowCreditThreshold">
                            <div class="form-group required">
                                <label>Low Credit Threshold (RM)</label>

                                <input type="text" data-vv-as="low credit threshold" name="lowCreditThreshold" data-vv-validate-on="blur"
                                    placeholder="Low Credit Threshold (RM)" v-model.number="companyCreditThreshold.value" v-validate="`required|decimal:2|min_value:${companyCreditThreshold.default}`"
                                    :class="{
                                        'form-control': true,
                                        'is-invalid':  validationErrors.has('formLowCreditThreshold.lowCreditThreshold')
                                    }">

                                <div v-show="validationErrors.has('formLowCreditThreshold.lowCreditThreshold')" class="invalid-feedback">
                                    {{ validationErrors.first('formLowCreditThreshold.lowCreditThreshold') }}
                                </div>
                            </div>
                            <p v-if="companyCreditThreshold.isSaving">
                                 {{ companyCreditThreshold.saveMessage }}
                            </p>
                            <div>
                                <button class="btn btn-primary btn-primary-insv" type="submit">Save Changes</button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>

        <div ref="tableSection">
            <h5 class="mb-3">Credit Balance History</h5>
            <table ref="listTable" class="table table-hover">
                <colgroup>
                    <col width="40">
                    <col width="200">
                    <col width="140">
                    <col>
                </colgroup>
                <thead>
                    <tr>
                        <th scope="col">#</th>
                        <th scope="col">Action Date</th>
                        <th scope="col" class="text-right">Amount (RM)</th>
                        <th scope="col">Remarks</th>
                    </tr>
                </thead>

                <tbody>
                    <tr v-show="historyList.length === 0">
                        <td :colspan="columnsCount" class="text-center font-italic">
                            {{isTableLoading ? "Loading Data": "No Data"}}
                        </td>
                    </tr>
                    <tr v-for="(historyItem, index) in historyList" :key="index">
                        <th scope="row">{{((pagination.currentPage - 1) *pagination.pageSize ) + index + 1}}</th>
                        <td class="text-nowrap">{{historyItem.date}}</td>
                        <td class="text-nowrap text-right">
                            {{historyItem.amount}}
                        </td>
                        <td class="text-nowrap">
                            {{historyItem.remarks}}
                        </td>
                    </tr>
                </tbody>
            </table>
            <pagination v-model="pagination.currentPage"
                :totalPages="pagination.totalPages"
                :scrollPosition="pagination.scrollPosition">
            </pagination>
        </div>

         <popup-modal ref="popupTopupCompanyCredit" :hasCloseButton="false" title="Add Credit to Company">
            <template slot="content">
                <div class="form-row">
                    <div class="form-group col">
                        <div><b>Note: </b></div>
                        <p>
                            Each deposited company credit is subjected to <b>RM5</b> payment gateway charge, of which this expense is deducted from your deposited amount
                        </p>
                        <p>
                            An invoice will be issued to you via your company's registered email address later.
                        </p>
                        <form data-vv-scope="company-credit-topup" @submit.prevent="validateTopup" ref="topupForm" method="post" :action="topupSubmitPath">
                            <label>Topup Amount</label>
                            <input ref="topupAmount" name="Amount" type="text" data-vv-validate-on="blur"
                                data-vv-as="topup amount" v-validate="'required|decimal:2|min_value:300.00'" v-model="topupForm.amount" :class="{
                                'form-control': true,
                                'is-invalid':  validationErrors.has('company-credit-topup.Amount')
                                }">
                            <input name="Token" v-model="token" type="hidden">
                            <div v-show="validationErrors.has('company-credit-topup.Amount')" class="invalid-feedback">
                                {{ validationErrors.first('company-credit-topup.Amount') }}
                            </div>
                        </form>
                    </div>
                </div>
            </template>
            <template slot="footer">
                <button type="button" class="btn btn-primary btn-primary-insv" @click="validateAndRedirectToPayment">Add Credit</button>
                <button type="button" class="btn btn-secondary" @click="dismissAddCredit">Cancel</button>
            </template>
        </popup-modal>

        <popup-modal ref="popupPaymentStatus" :hasCloseButton="true" :title="paymentPrompt.paymentStatusTitle">
           <template slot="content">
               <p>{{paymentPrompt.paymentStatusDetails}}</p>
           </template>
           <template slot="footer">
               <button @click="dismissPaymentStatus" class="btn btn-primary btn-primary-insv">Okay</button>
           </template>
        </popup-modal>
    </div>
</template>

<script>
import pagination from '@/components/pagination';
import popupModal from '@/components/popup-modal';
import {
    apiPath
} from '@/config';
import {
    convertToUserReadableDateTime
} from '@/utilities';
import Cookies from 'js-cookie';
export default {
    'name': 'CompanyWallet',
    'components': {
        popupModal,
        pagination
    },
    mounted () {
        this.columnsCount = this.$refs.listTable.rows[0].cells.length;
        this.getCompanyCredit();
        this.getCompanyCreditLog();

        if (this.$route.query && this.$route.query.status) {
            if (this.$route.query.status === '1') {
                this.paymentPrompt.paymentStatusTitle = 'Payment Successful!';
                this.paymentPrompt.paymentStatusDetails = 'Thank you, your payment has been successfully completed!';
            } else if (this.$route.query.status === '6') {
                this.paymentPrompt.paymentStatusTitle = 'Payment Pending.';
                this.paymentPrompt.paymentStatusDetails = 'Thank you, your payment status is pending.';
            } else {
                this.paymentPrompt.paymentStatusTitle = 'Payment Unsuccessful';
                this.paymentPrompt.paymentStatusDetails = 'Sorry, your payment was unsuccessful. Please try again.';
            }

            this.$refs.popupPaymentStatus.show();
        }

        // initialize scroll position
        this.$nextTick(() => {
            this.pagination.scrollPosition = this.$refs.tableSection.getBoundingClientRect().top + window.scrollY - document.querySelector('.top-navigation').clientHeight;
        });
    },
    'computed': {
        topupSubmitPath () {
            return `${apiPath}MakePayment/TopupCompanyCredit`;
        },
        currentPage () {
            return this.pagination.currentPage;
        },
        token () {
            return Cookies.get('t');
        }
    },
    'watch': {
        currentPage () {
            this.$nextTick(() => {
                this.getCompanyCreditLog();
            });
        }
    },
    data () {
        return {
            'creditBalance': 0,
            'topupForm': {
                'amount': 100
            },
            'paymentPrompt': {
                'paymentStatusTitle': '',
                'paymentStatusDetails': ''
            },
            'historyList': [],
            'columnsCount': 1,
            'isTableLoading': true,
            'pagination': {
                'currentPage': 1,
                'totalPages': 0,
                'pageSize': 20,
                'scrollPosition': 0
            },
            'companyCreditThreshold': {
                'default': 0,
                'value': 0,
                'saveMessage': null,
                'isSaving': false
            }
        };
    },
    'methods': {
        getCompanyCredit () {
            this.axios.get('CompanyCredit/GetBalance').then(({
                data
            }) => {
                this.creditBalance = data.data.a;
                this.companyCreditThreshold.default = data.data.dt;
                this.companyCreditThreshold.value = data.data.lt;
            });
        },
        initiateAddCredit () {
            this.topupForm.amount = 0;

            this.$refs.popupTopupCompanyCredit.show().then(() => {
                this.$nextTick(() => {
                    this.$refs.topupAmount.select();
                });
            });
        },
        dismissAddCredit () {
            this.$refs.popupTopupCompanyCredit.hide();
        },
        validateTopup () {
            return this.$validator.validateAll('company-credit-topup');
        },
        validateAndRedirectToPayment () {
            this.validateTopup().then((isValid) => {
                if (isValid) {
                    this.$refs.topupForm.submit();
                }
            });
        },
        dismissPaymentStatus () {
            this.$refs.popupPaymentStatus.hide();
        },
        getCompanyCreditLog () {
            this.axios.post('CompanyCredit/GetHistory', {
                'p': this.pagination.currentPage,
                'ps': this.pagination.pageSize
            }).then(({
                data
            }) => {
                this.isTableLoading = false;
                let pagedResult = data.data;

                this.pagination.currentPage = pagedResult.p;
                this.pagination.totalPages = pagedResult.c;

                let list = pagedResult.l;
                this.historyList = list.map((item) => ({
                    'amount': `${item.a.toFixed(2)}`,
                    'date': item.d ? convertToUserReadableDateTime(item.d) : '-',
                    'remarks': item.r ? item.r : '-'
                }));
            });
        },
        submitCompanyLowCreditThreshold () {
            this.$validator.validateAll('formLowCreditThreshold').then((isValid) => {
                if (isValid) {
                    this.companyCreditThreshold.isSaving = true;
                    this.companyCreditThreshold.saveMessage = 'Updating low credit balance threshold...';

                    this.axios.post('CompanyCredit/UpdateLowCreditNotificationThreshold', {
                        'lt': this.companyCreditThreshold.value
                    }).then(() => {
                        this.companyCreditThreshold.saveMessage = 'Low credit balance threshold updated!';
                    }).catch(() => {
                        this.companyCreditThreshold.saveMessage = 'Error updating low credit balance threshold, please try again.';
                    });
                }
            });
        }
    }
};

</script>

<style>
    .ewallet-display:not(:last-child) {
        padding: 0 0 1.25rem;
    }

    .ewallet-display {
        font-size: 2.5rem;
        font-weight: 300;
    }

</style>
